import { ensureImageSizeAsync } from 'services/image-utils';

/**
 * Shared photo-library upload for the AI scanner pages (owner decision
 * #13 in design-docs/scanner-shared-components.md: one file-picker
 * helper for all scanners, using the safer body-append + focus-cleanup
 * variant that meal/equipment already had).
 *
 * Opens the OS file picker, reads the chosen image, draws it onto a
 * canvas, and resizes it to the scanner image budget
 * (`ensureImageSizeAsync`). The page receives the processed photo in
 * `onPicked` and does its own state update + telemetry there.
 *
 * Read/processing failures are logged and the pick is dropped — the
 * user simply stays on the camera and can try again (moved verbatim
 * from the pages; a failed local file read needs no telemetry).
 */
export function openScannerImagePicker(options: {
  /** The page's `safeTimeout` (CsPage) so the cancel-cleanup timer dies
   *  with the page. */
  safeTimeout: (callback: () => void, ms: number) => void;
  /** Max images to accept from one pick — the scanner's REMAINING slots. Defaults to 1
   *  (single-select). When > 1 the OS picker allows multi-select; any files the user picks
   *  beyond this limit are ignored so the scanner's per-image cap is never exceeded. */
  maxFiles?: number;
  /** Receives each processed photo after a successful pick. For a multi-select the helper
   *  calls this once per image, in order, awaiting each call so the page's per-photo state
   *  update and reel animation stay consistent. */
  onPicked: (photo: { dataUrl: string; base64: string }) => void | Promise<void>;
}) {
  const maxFiles = Math.max(1, options.maxFiles ?? 1);
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  // if more than one slot is left to fill, allow multi-select in the OS picker
  if (maxFiles > 1) input.multiple = true;
  input.style.display = 'none';
  document.body.appendChild(input);

  // if the user cancels the picker, clean up the input on next focus return
  const cleanup = () => {
    window.removeEventListener('focus', cleanup);
    options.safeTimeout(() => { if (input.parentNode) input.remove(); }, 500);
  };

  input.onchange = () => {
    // take only up to the remaining-slot limit; ignore any extra picks
    const files = Array.from(input.files ?? []).slice(0, maxFiles);
    input.remove();
    cleanup();
    if (files.length === 0) return;
    void handlePickedFilesAsync(files, options.onPicked);
  };

  window.addEventListener('focus', cleanup);
  input.click();
}

// for each picked image, process it and hand it back IN ORDER — awaiting onPicked between
// images keeps the page's slot-index / capturedPhotos update and reel animation consistent.
// if one image fails to read or resize it is dropped and the rest still load (a bad local
// file is the user's pick, not an app fault, so no telemetry — same policy as single-pick).
// the page's own onPicked stays OUTSIDE that catch: a throw there is an app bug, and it
// travels up to the unhandled-rejection report instead of passing for a bad file (#5191)
async function handlePickedFilesAsync(
  files: File[],
  onPicked: (photo: { dataUrl: string; base64: string }) => void | Promise<void>,
) {
  for (const file of files) {
    let photo: { dataUrl: string; base64: string };
    try {
      photo = await processPickedImageFileAsync(file);
    } catch (err) {
      console.log('Failed to process uploaded image, skipping it (a bad picked file, not an app fault):', err);
      continue;
    }
    await onPicked(photo);
  }
}

// read the picked file and resize it to the scanner image budget
async function processPickedImageFileAsync(file: File): Promise<{ dataUrl: string; base64: string }> {
  const dataUrl = await readFileAsDataUrlAsync(file);
  const img = await loadImageAsync(dataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  canvas.getContext('2d').drawImage(img, 0, 0);
  return ensureImageSizeAsync(canvas);
}

function readFileAsDataUrlAsync(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read uploaded file'));
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
}

function loadImageAsync(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}
